import { ButtonGroup, FormControlSizes, HStack, Text, ToggleButton } from '@talos/kyoko';
import { SignalsCurrencySelect } from '../core';
import { ExchangeSelect } from './ExchangeSelect';
import type { FundingRateFrequency, FundingRateMAWindow, FundingRateTimeRange } from './HistoricalFundingRate.types';
import {
  FUNDING_RATE_FREQUENCIES,
  FUNDING_RATE_MA_WINDOWS,
  FUNDING_RATE_TIME_RANGES,
  FundingRateScope,
} from './HistoricalFundingRate.types';
import {
  FUNDING_RATE_FREQUENCY_LABELS,
  FUNDING_RATE_MA_WINDOW_LABELS,
  isFundingRateFrequencyRangeDisallowed,
} from './tokens';

interface HistoricalFundingRateToolbarLeftProps {
  scope: FundingRateScope;
  onScopeChange: (scope: FundingRateScope) => void;
  availableTalosSymbols: string[];
  selectedTalosSymbol: string | undefined;
  onSelectedTalosSymbolChange: (symbol: string | undefined) => void;
  frequency: FundingRateFrequency;
  onFrequencyChange: (frequency: FundingRateFrequency) => void;
  /** Current range — a frequency is disabled when it would be invalid for this range. */
  timeRange: FundingRateTimeRange;
  isLoading: boolean;
  availableExchanges: string[];
  selectedExchanges: string[];
  onSelectedExchangesChange: (exchanges: string[]) => void;
  exchangeNameByExchangeId: ReadonlyMap<string, string>;
}

interface HistoricalFundingRateToolbarRightProps {
  maWindow: FundingRateMAWindow;
  onMAWindowChange: (window: FundingRateMAWindow) => void;
  timeRange: FundingRateTimeRange;
  onTimeRangeChange: (range: FundingRateTimeRange) => void;
  /** Current frequency — a range is disabled when it would be invalid for this frequency. */
  frequency: FundingRateFrequency;
  disabled?: boolean;
}

export type HistoricalFundingRateToolbarProps = HistoricalFundingRateToolbarLeftProps &
  HistoricalFundingRateToolbarRightProps;

/** Left slot of the HistoricalFundingRate toolbar: scope | asset | exchanges (ByExchange only) | frequency. */
export function HistoricalFundingRateToolbarLeft({
  scope,
  onScopeChange,
  availableTalosSymbols,
  selectedTalosSymbol,
  onSelectedTalosSymbolChange,
  frequency,
  onFrequencyChange,
  timeRange,
  isLoading,
  availableExchanges,
  selectedExchanges,
  onSelectedExchangesChange,
  exchangeNameByExchangeId,
}: HistoricalFundingRateToolbarLeftProps) {
  return (
    <HStack gap="spacing8">
      <ButtonGroup size={FormControlSizes.Small} data-testid="funding-rate-scope-toggle">
        <ToggleButton
          selected={scope === FundingRateScope.AssetLevel}
          onClick={() => onScopeChange(FundingRateScope.AssetLevel)}
          data-testid="funding-rate-scope-asset-level"
        >
          Asset Level
        </ToggleButton>
        <ToggleButton
          selected={scope === FundingRateScope.ByExchange}
          onClick={() => onScopeChange(FundingRateScope.ByExchange)}
          data-testid="funding-rate-scope-by-exchange"
        >
          By Exchange
        </ToggleButton>
      </ButtonGroup>
      <SignalsCurrencySelect
        options={availableTalosSymbols}
        value={selectedTalosSymbol}
        onChange={onSelectedTalosSymbolChange}
        prefixLabel="Asset"
        disabled={isLoading}
        width="160px"
      />
      {scope === FundingRateScope.ByExchange && (
        <ExchangeSelect
          availableExchanges={availableExchanges}
          selectedExchanges={selectedExchanges}
          onSelectedExchangesChange={onSelectedExchangesChange}
          exchangeNameByExchangeId={exchangeNameByExchangeId}
          width="180px"
        />
      )}
      <ButtonGroup size={FormControlSizes.Small} data-testid="funding-rate-frequency-toggle">
        {FUNDING_RATE_FREQUENCIES.map(freq => (
          <ToggleButton
            key={freq}
            selected={frequency === freq}
            onClick={() => onFrequencyChange(freq)}
            disabled={isFundingRateFrequencyRangeDisallowed(freq, timeRange)}
            data-testid={`funding-rate-frequency-${freq}`}
          >
            {FUNDING_RATE_FREQUENCY_LABELS[freq]}
          </ToggleButton>
        ))}
      </ButtonGroup>
    </HStack>
  );
}

/** Right slot of the HistoricalFundingRate toolbar: moving average | time range. */
export function HistoricalFundingRateToolbarRight({
  maWindow,
  onMAWindowChange,
  timeRange,
  onTimeRangeChange,
  frequency,
  disabled,
}: HistoricalFundingRateToolbarRightProps) {
  return (
    <HStack gap="spacing8">
      <HStack gap="spacing4">
        <Text color="colorTextSubtle" fontSize="fontSizeSmall">
          Moving Average
        </Text>
        <ButtonGroup size={FormControlSizes.Small} data-testid="funding-rate-ma-toggle">
          {FUNDING_RATE_MA_WINDOWS.map(w => (
            <ToggleButton
              key={w}
              selected={maWindow === w}
              onClick={() => onMAWindowChange(w)}
              disabled={disabled}
              data-testid={`funding-rate-ma-${w}`}
            >
              {FUNDING_RATE_MA_WINDOW_LABELS[w]}
            </ToggleButton>
          ))}
        </ButtonGroup>
      </HStack>
      <ButtonGroup size={FormControlSizes.Small} data-testid="funding-rate-time-range-control">
        {FUNDING_RATE_TIME_RANGES.map(range => (
          <ToggleButton
            key={range}
            selected={timeRange === range}
            onClick={() => onTimeRangeChange(range)}
            disabled={disabled || isFundingRateFrequencyRangeDisallowed(frequency, range)}
            data-testid={`funding-rate-time-range-${range}`}
          >
            {range}
          </ToggleButton>
        ))}
      </ButtonGroup>
    </HStack>
  );
}
